import { Icon as IconifyIcon } from "@iconify/react";
import type { ComponentProps } from "react";
import { solarIcons, type SolarIconName } from "@papier/ui/lib/solar";

export type IconProps = Omit<ComponentProps<typeof IconifyIcon>, "icon" | "ssr"> & {
  size?: string | number;
};
export type IconComponent = (props: IconProps) => React.JSX.Element;
export type { SolarIconName };

/** Locally bundled Solar vectors render immediately, including offline and on the server. */
export function Icon({
  icon,
  size = 24,
  width = size,
  height = size,
  ...props
}: IconProps & { icon: SolarIconName }) {
  return (
    <IconifyIcon
      icon={{ width: 24, height: 24, ...solarIcons[icon] }}
      ssr
      width={width}
      height={height}
      data-icon={icon}
      aria-hidden={props["aria-label"] || props["aria-labelledby"] ? false : true}
      {...props}
    />
  );
}

function createIcon(icon: SolarIconName): IconComponent {
  return function SolarIcon(props: IconProps) {
    return <Icon icon={icon} {...props} />;
  };
}

export const Add = createIcon("solar:add-bold-duotone");
export const AddCircle = createIcon("solar:add-circle-bold-duotone");
export const AltArrowDown = createIcon("solar:alt-arrow-down-bold-duotone");
export const AltArrowLeft = createIcon("solar:alt-arrow-left-bold-duotone");
export const AltArrowRight = createIcon("solar:alt-arrow-right-bold-duotone");
export const AltArrowUp = createIcon("solar:alt-arrow-up-bold-duotone");
export const Archive = createIcon("solar:archive-bold-duotone");
export const ArrowDown = createIcon("solar:arrow-down-bold-duotone");
export const ArrowLeft = createIcon("solar:arrow-left-bold-duotone");
export const ArrowLeftDown = createIcon("solar:arrow-left-down-bold-duotone");
export const ArrowRight = createIcon("solar:arrow-right-bold-duotone");
export const ArrowRightUp = createIcon("solar:arrow-right-up-bold-duotone");
export const ArrowToDownLeft = createIcon("solar:arrow-to-down-left-bold-duotone");
export const ArrowUp = createIcon("solar:arrow-up-bold-duotone");
export const BatteryFull = createIcon("solar:battery-full-bold-duotone");
export const Bell = createIcon("solar:bell-bold-duotone");
export const BellRing = createIcon("solar:bell-ring-bold-duotone");
export const Bolt = createIcon("solar:bolt-bold-duotone");
export const Book2 = createIcon("solar:book-2-bold-duotone");
export const Braces = createIcon("solar:braces-bold-duotone");
export const Buildings = createIcon("solar:buildings-bold-duotone");
export const Buildings2 = createIcon("solar:buildings-2-bold-duotone");
export const Calendar = createIcon("solar:calendar-bold-duotone");
export const Camera = createIcon("solar:camera-bold-duotone");
export const Card = createIcon("solar:card-bold-duotone");
export const ChatSquare = createIcon("solar:chat-square-bold-duotone");
export const Check = createIcon("solar:check-bold-duotone");
export const CheckCircle = createIcon("solar:check-circle-bold-duotone");
export const Clipboard = createIcon("solar:clipboard-bold-duotone");
export const ClipboardText = createIcon("solar:clipboard-text-bold-duotone");
export const ClockCircle = createIcon("solar:clock-circle-bold-duotone");
export const Close = createIcon("solar:close-bold-duotone");
export const CloseCircle = createIcon("solar:close-circle-bold-duotone");
export const CloudUpload = createIcon("solar:cloud-upload-bold-duotone");
export const Code = createIcon("solar:code-bold-duotone");
export const Code2 = createIcon("solar:code-2-bold-duotone");
export const Copy = createIcon("solar:copy-bold-duotone");
export const Crop = createIcon("solar:crop-bold-duotone");
export const DangerCircle = createIcon("solar:danger-circle-bold-duotone");
export const DangerTriangle = createIcon("solar:danger-triangle-bold-duotone");
export const Database = createIcon("solar:database-bold-duotone");
export const DocumentText = createIcon("solar:document-text-bold-duotone");
export const DollarMinimalistic = createIcon("solar:dollar-minimalistic-bold-duotone");
export const DoubleAltArrowLeft = createIcon("solar:double-alt-arrow-left-bold-duotone");
export const DoubleAltArrowRight = createIcon("solar:double-alt-arrow-right-bold-duotone");
export const Download = createIcon("solar:download-bold-duotone");
export const Eraser = createIcon("solar:eraser-bold-duotone");
export const Eye = createIcon("solar:eye-bold-duotone");
export const EyeClosed = createIcon("solar:eye-closed-bold-duotone");
export const File = createIcon("solar:file-bold-duotone");
export const FileImage = createIcon("solar:file-image-bold-duotone");
export const FilePen = createIcon("solar:file-pen-bold-duotone");
export const FileText = createIcon("solar:file-text-bold-duotone");
export const Filter = createIcon("solar:filter-bold-duotone");
export const Folder = createIcon("solar:folder-bold-duotone");
export const FolderOpen = createIcon("solar:folder-open-bold-duotone");
export const ForbiddenCircle = createIcon("solar:forbidden-circle-bold-duotone");
export const Forward = createIcon("solar:forward-bold-duotone");
export const Gallery = createIcon("solar:gallery-bold-duotone");
export const GitBranch = createIcon("solar:git-branch-bold-duotone");
export const GitDiff = createIcon("solar:git-diff-bold-duotone");
export const GraphUp = createIcon("solar:graph-up-bold-duotone");
export const GripVertical = createIcon("solar:grip-vertical-bold-duotone");
export const Hashtag = createIcon("solar:hashtag-bold-duotone");
export const History = createIcon("solar:history-bold-duotone");
export const Home2 = createIcon("solar:home-2-bold-duotone");
export const Hourglass = createIcon("solar:hourglass-bold-duotone");
export const InfoCircle = createIcon("solar:info-circle-bold-duotone");
export const Inbox = createIcon("solar:inbox-bold-duotone");
export const KeyMinimalistic = createIcon("solar:key-minimalistic-bold-duotone");
export const Laptop = createIcon("solar:laptop-bold-duotone");
export const Layers = createIcon("solar:layers-bold-duotone");
export const Letter = createIcon("solar:letter-bold-duotone");
export const LetterOpened = createIcon("solar:letter-opened-bold-duotone");
export const List = createIcon("solar:list-bold-duotone");
export const LockKeyhole = createIcon("solar:lock-keyhole-bold-duotone");
export const Login2 = createIcon("solar:login-2-bold-duotone");
export const Logout2 = createIcon("solar:logout-2-bold-duotone");
export const Magnifier = createIcon("solar:magnifier-bold-duotone");
export const MagnifierClose = createIcon("solar:magnifier-close-bold-duotone");
export const MenuDots = createIcon("solar:menu-dots-bold-duotone");
export const MenuDotsVertical = createIcon("solar:menu-dots-vertical-bold-duotone");
export const Microphone = createIcon("solar:microphone-bold-duotone");
export const Minus = createIcon("solar:minus-bold-duotone");
export const Monitor = createIcon("solar:monitor-bold-duotone");
export const MonitorSmartphone = createIcon("solar:monitor-smartphone-bold-duotone");
export const Moon = createIcon("solar:moon-bold-duotone");
export const PanelLeft = createIcon("solar:panel-left-bold-duotone");
export const Pen = createIcon("solar:pen-bold-duotone");
export const Phone = createIcon("solar:phone-bold-duotone");
export const Plain = createIcon("solar:plain-bold-duotone");
export const PlugCircle = createIcon("solar:plug-circle-bold-duotone");
export const Pulse = createIcon("solar:pulse-bold-duotone");
export const QrCode = createIcon("solar:qr-code-bold-duotone");
export const QuestionCircle = createIcon("solar:question-circle-bold-duotone");
export const Record = createIcon("solar:record-bold-duotone");
export const Refresh = createIcon("solar:refresh-bold-duotone");
export const Repeat = createIcon("solar:repeat-bold-duotone");
export const Reply = createIcon("solar:reply-bold-duotone");
export const Restart = createIcon("solar:restart-bold-duotone");
export const Routing = createIcon("solar:routing-bold-duotone");
export const Server = createIcon("solar:server-bold-duotone");
export const Settings = createIcon("solar:settings-bold-duotone");
export const SettingsMinimalistic = createIcon("solar:settings-minimalistic-bold-duotone");
export const Shield = createIcon("solar:shield-bold-duotone");
export const ShieldCheck = createIcon("solar:shield-check-bold-duotone");
export const ShieldKeyhole = createIcon("solar:shield-keyhole-bold-duotone");
export const ShieldWarning = createIcon("solar:shield-warning-bold-duotone");
export const SlashCircle = createIcon("solar:slash-circle-bold-duotone");
export const SliderHorizontal = createIcon("solar:slider-horizontal-bold-duotone");
export const Smartphone = createIcon("solar:smartphone-bold-duotone");
export const SortVertical = createIcon("solar:sort-vertical-bold-duotone");
export const Soundwave = createIcon("solar:soundwave-bold-duotone");
export const SquareArrowRightUp = createIcon("solar:square-arrow-right-up-bold-duotone");
export const Stars = createIcon("solar:stars-bold-duotone");
export const Sun = createIcon("solar:sun-bold-duotone");
export const Tablet = createIcon("solar:tablet-bold-duotone");
export const TextField = createIcon("solar:text-field-bold-duotone");
export const TransferHorizontal = createIcon("solar:transfer-horizontal-bold-duotone");
export const Translation = createIcon("solar:translation-bold-duotone");
export const TrashBinTrash = createIcon("solar:trash-bin-trash-bold-duotone");
export const Tuning = createIcon("solar:tuning-bold-duotone");
export const UndoLeftRound = createIcon("solar:undo-left-round-bold-duotone");
export const Upload = createIcon("solar:upload-bold-duotone");
export const User = createIcon("solar:user-bold-duotone");
export const UserCircle = createIcon("solar:user-circle-bold-duotone");
export const UserCross = createIcon("solar:user-cross-bold-duotone");
export const UserCrossRounded = createIcon("solar:user-cross-rounded-bold-duotone");
export const UserId = createIcon("solar:user-id-bold-duotone");
export const UserMinusRounded = createIcon("solar:user-minus-rounded-bold-duotone");
export const UserPlus = createIcon("solar:user-plus-bold-duotone");
export const UserPlusRounded = createIcon("solar:user-plus-rounded-bold-duotone");
export const UserRounded = createIcon("solar:user-rounded-bold-duotone");
export const UsersGroupRounded = createIcon("solar:users-group-rounded-bold-duotone");
export const Videocamera = createIcon("solar:videocamera-bold-duotone");
export const WiFi = createIcon("solar:wi-fi-bold-duotone");
export const Widget = createIcon("solar:widget-bold-duotone");
export const Widget2 = createIcon("solar:widget-2-bold-duotone");
